import React from "react";
import { ApiResponseStatistics } from "../../api/types/api-response-statistics";
import { LockOpen } from "lucide-react";
import { TaggingStatusIcon } from "./tagging-status-icon";
import { Link, useNavigate } from "react-router-dom";
import { useGetPageUrl } from "../../utils/use-get-page-url";
import { formatCount, formatCountOr } from "../../utils/format";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";

export const StatisticsTable: React.FC<{
  data: ApiResponseStatistics[];
  isSupervisorMode: boolean;
  onUnlockComments: (id: number) => void;
}> = (props) => {
  const { getResponsesUrl } = useGetPageUrl();
  const navigate = useNavigate();
  const totalCount = React.useMemo(() => {
    return props.data.reduce((acc, row) => acc + row.attributes.value, 0);
  }, [props.data]);

  const handleRowClick = (row: ApiResponseStatistics) => {
    const url = getResponsesUrl({
      organizationId: String(row.attributes.organization_id),
      themeStructureId: String(row.attributes.theme_structure_id),
    });
    navigate(url);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Theme structure ID</TableHead>
            <TableHead>Theme structure</TableHead>
            <TableHead>Tagging status</TableHead>
            <TableHead>Untagged With guesses</TableHead>
            <TableHead>Untagged without Guesses (Processed)</TableHead>
            <TableHead>Untagged without Guesses (Unprocessed)</TableHead>
            <TableHead>Untagged Skipped</TableHead>
            <TableHead>Retags</TableHead>
            <TableHead>Total Untagged</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {props.data.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={12}
                className="text-center text-muted-foreground py-8"
              >
                No organizations found
              </TableCell>
            </TableRow>
          ) : (
            <>
              {props.data
                ?.filter((row) => row.attributes.value > 0)
                .map((row) => (
                  <TableRow
                    key={row.attributes.organization_id}
                    onClick={() => handleRowClick(row)}
                    className="cursor-pointer"
                  >
                    <TableCell>{row.attributes.organization_id}</TableCell>
                    <TableCell>{row.attributes.organization}</TableCell>
                    <TableCell>{row.attributes.theme_structure_id}</TableCell>
                    <TableCell>{row.attributes.theme_structure}</TableCell>
                    <TableCell>
                      <TaggingStatusIcon
                        status={row.attributes.tagging_status}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={getResponsesUrl({
                          organizationId: String(
                            row.attributes.organization_id,
                          ),
                          themeStructureId: String(
                            row.attributes.theme_structure_id,
                          ),
                          filters: {
                            guesses: true,
                          },
                        })}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {formatCountOr(row.attributes.with_guesses, "—")}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={getResponsesUrl({
                          organizationId: String(
                            row.attributes.organization_id,
                          ),
                          themeStructureId: String(
                            row.attributes.theme_structure_id,
                          ),
                          filters: {
                            processed: true,
                          },
                        })}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {formatCountOr(
                          row.attributes.without_guesses_processed,
                          "—",
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={getResponsesUrl({
                          organizationId: String(
                            row.attributes.organization_id,
                          ),
                          themeStructureId: String(
                            row.attributes.theme_structure_id,
                          ),
                          filters: {
                            processed: false,
                          },
                        })}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {formatCountOr(
                          row.attributes.without_guesses_unprocessed,
                          "—",
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={getResponsesUrl({
                          organizationId: String(
                            row.attributes.organization_id,
                          ),
                          themeStructureId: String(
                            row.attributes.theme_structure_id,
                          ),
                          filters: {
                            skipped: true,
                          },
                        })}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {formatCountOr(row.attributes.skipped, "—")}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={getResponsesUrl({
                          organizationId: String(
                            row.attributes.organization_id,
                          ),
                          themeStructureId: String(
                            row.attributes.theme_structure_id,
                          ),
                          filters: {
                            retags: true,
                          },
                        })}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {formatCountOr(row.attributes.retags, "—")}
                      </Link>
                    </TableCell>
                    <TableCell>{formatCount(row.attributes.value)}</TableCell>
                    <TableCell>
                      {!props.isSupervisorMode &&
                        row.attributes.locked_count > 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              props.onUnlockComments(
                                row.attributes.organization_id,
                              );
                            }}
                          >
                            <LockOpen className="h-4 w-4" />
                          </Button>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              <TableRow>
                <TableCell colSpan={10} className="font-bold">
                  Total
                </TableCell>
                <TableCell className="font-bold">
                  {formatCount(totalCount)}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
